import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { LayoutDashboard, LogOut } from 'lucide-react';

const PrivacyPolicy = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const AuthNavButton = ({ isMobile = false }) => {
    if (isAuthenticated) {
      return (
        <div className="flex items-center gap-4">
          <Link
            to="/painel"
            className={`flex items-center gap-2 font-medium transition-colors ${
              isMobile 
                ? 'bg-[#A56096] text-white px-4 py-2 rounded-full text-sm' 
                : 'text-[#3A5859] hover:text-[#A56096]'
            }`}
          >
            <LayoutDashboard className="h-4 w-4" />
            {`Painel`}
          </Link>
          <button
            onClick={handleLogout}
            title="Sair"
            className={`flex items-center justify-center p-2 rounded-full transition-colors ${
              isMobile
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-red-100 text-red-600 hover:bg-red-200'
            }`}
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      );
    }

    return (
      <Link 
        to="/login" 
        className={`bg-[#A56096] text-white rounded-full hover:bg-[#945586] transition-colors font-medium ${
          isMobile ? 'px-4 py-2 text-sm' : 'px-6 py-2'
        }`}
      >
        Entrar
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#F3F6F1]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-[#F3F6F1]/95 backdrop-blur-md border-b border-[#E5E9E2] z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/">
                <img 
                  src="/assets/logo.png" 
                  alt="Constela Brasil" 
                  className="h-10 w-auto"
                />
              </Link>
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-[#3A5859] hover:text-[#A56096] font-medium transition-colors">
                Home
              </Link>
              <Link to="/planos" className="text-[#3A5859] hover:text-[#A56096] font-medium transition-colors">
                Planos
              </Link>
              <AuthNavButton />
            </nav>

            <div className="md:hidden">
              <AuthNavButton isMobile={true} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#3A5859] mb-4">
              Política de Privacidade
            </h1>
            <p className="text-xl text-[#496566] max-w-3xl mx-auto">
              Seu compromisso com a proteção dos seus dados pessoais
            </p>
            <p className="text-sm text-[#496566] mt-4">
              Última atualização: Janeiro de 2025
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              
              {/* Introdução */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">1. Introdução e Compromisso</h2>
                <p className="text-[#496566] mb-4">
                  A <strong>Constela Brasil</strong> está comprometida com a proteção da sua privacidade e dos seus dados pessoais. 
                  Esta Política de Privacidade descreve como recolhemos, usamos, armazenamos e protegemos as suas informações 
                  pessoais, em conformidade com a Lei Geral de Proteção de Dados (LGPD) e outras legislações aplicáveis.
                </p>
                <p className="text-[#496566]">
                  Ao utilizar os nossos serviços, você concorda com as práticas descritas nesta política.
                </p>
              </section>

              {/* Dados que Recolhemos */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">2. Dados que Recolhemos</h2>
                
                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">2.1 Informações de Registo</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Nome completo</li>
                  <li>Endereço de e-mail</li>
                  <li>Número de telefone</li>
                  <li>Data de nascimento</li>
                  <li>Informações sobre o tipo de utilizador (participante, constelador)</li>
                  <li>Credenciais de acesso (palavra-passe encriptada)</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">2.2 Dados de Pagamento</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Informações de cartão de crédito/débito (processadas por terceiros seguros)</li>
                  <li>Histórico de transações</li>
                  <li>Dados de faturação</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">2.3 Dados de Utilização</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Registos de acesso e utilização da plataforma</li>
                  <li>Informações técnicas do dispositivo e navegador</li>
                  <li>Endereços IP</li>
                  <li>Páginas visitadas e tempo de permanência</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">2.4 Informações das Sessões</h3>
                <ul className="list-disc pl-6 text-[#496566]">
                  <li>Dados de participação em constelações</li>
                  <li>Comunicações durante as sessões (quando aplicável)</li>
                  <li>Avaliações e feedback sobre os serviços</li>
                </ul>
              </section>

              {/* Como Usamos os Seus Dados */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">3. Como Usamos os Seus Dados</h2>
                
                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">3.1 Fornecimento do Serviço</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Criar e gerenciar a sua conta de usuário</li>
                  <li>Facilitar a participação em sessões de constelação</li>
                  <li>Conectar participantes e consteladores</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">3.2 Processamento de Pagamentos</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Processar pagamentos de planos e sessões</li>
                  <li>Emitir recibos e faturas</li>
                  <li>Gerenciar reembolsos quando aplicável</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">3.3 Comunicação</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Enviar notificações sobre sessões agendadas</li>
                  <li>Comunicar alterações nos nossos serviços</li>
                  <li>Responder a questões e solicitações de suporte</li>
                  <li>Enviar informações promocionais (com o seu consentimento)</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">3.4 Melhorias na Plataforma</h3>
                <ul className="list-disc pl-6 text-[#496566]">
                  <li>Analisar padrões de utilização para melhorar a experiência</li>
                  <li>Desenvolver novos recursos e funcionalidades</li>
                  <li>Garantir a segurança e estabilidade da plataforma</li>
                </ul>
              </section>

              {/* Uso de Serviços do Google */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">4. Uso de Serviços do Google</h2>
                <p className="text-[#496566] mb-4">
                  A plataforma Constela Brasil utiliza serviços do Google para oferecer funcionalidades essenciais. 
                  Esta seção descreve detalhadamente como acessamos, usamos e protegemos os dados obtidos através 
                  das APIs do Google, em conformidade com a{' '}
                  <a href="https://developers.google.com/terms/api-services-user-data-policy" 
                     target="_blank" 
                     rel="noopener noreferrer" 
                     className="text-[#A56096] hover:underline">
                    Política de Dados do Usuário dos Serviços da API do Google
                  </a>.
                </p>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">4.1 Dados do Google Acessados</h3>
                <p className="text-[#496566] mb-4">
                  Quando você opta por fazer login com sua conta Google, nossa plataforma solicita acesso aos seguintes dados:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><strong>Endereço de e-mail:</strong> Para criar e autenticar sua conta na plataforma</li>
                  <li><strong>Nome do perfil:</strong> Para personalizar sua experiência e identificação dentro da plataforma</li>
                  <li><strong>Eventos do Google Calendar (apenas para terapeutas):</strong> Para criar, visualizar e gerenciar agendamentos de sessões terapêuticas</li>
                </ul>
                <p className="text-[#496566] mb-4">
                  <strong>Escopos OAuth solicitados:</strong>
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><code className="bg-gray-100 px-2 py-1 rounded text-sm">userinfo.email</code> - Acesso ao endereço de e-mail</li>
                  <li><code className="bg-gray-100 px-2 py-1 rounded text-sm">userinfo.profile</code> - Acesso ao nome do perfil</li>
                  <li><code className="bg-gray-100 px-2 py-1 rounded text-sm">calendar.events</code> - Acesso para criar e gerenciar eventos no calendário (apenas terapeutas)</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">4.2 Como Usamos os Dados do Google</h3>
                
                <h4 className="text-lg font-semibold text-[#3A5859] mb-3">4.2.1 Autenticação com Google OAuth</h4>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><strong>Criação de conta:</strong> Utilizamos seu e-mail e nome do Google para criar automaticamente sua conta na plataforma, eliminando a necessidade de preencher formulários extensos</li>
                  <li><strong>Login simplificado:</strong> Permitimos que você acesse a plataforma usando suas credenciais do Google de forma segura</li>
                  <li><strong>Identificação:</strong> Usamos seu nome para personalizar a interface e identificá-lo durante as sessões</li>
                </ul>

                <h4 className="text-lg font-semibold text-[#3A5859] mb-3">4.2.2 Integração com Google Calendar (Terapeutas)</h4>
                <p className="text-[#496566] mb-4">
                  Para terapeutas que conectam suas contas do Google Calendar, utilizamos os dados para:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><strong>Sincronização de disponibilidade:</strong> Lemos seus eventos existentes para identificar horários já ocupados e evitar conflitos de agendamento</li>
                  <li><strong>Criação automática de eventos:</strong> Quando um cliente agenda uma sessão, criamos automaticamente um evento no seu Google Calendar com os detalhes da sessão</li>
                  <li><strong>Notificações nativas:</strong> O próprio Google envia notificações por e-mail e no celular quando sessões estão próximas de ocorrer</li>
                  <li><strong>Bloqueio de horários:</strong> Marcamos horários agendados como ocupados para evitar duplas reservas</li>
                </ul>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                  <p className="text-blue-800 text-sm">
                    <strong>Importante:</strong> O acesso ao Google Calendar é opcional e solicitado apenas para terapeutas. 
                    Clientes que participam das sessões não precisam conceder acesso ao calendário.
                  </p>
                </div>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">4.3 Armazenamento de Dados do Google</h3>
                <p className="text-[#496566] mb-4">
                  Armazenamos os seguintes dados obtidos através dos serviços do Google em nossos servidores seguros:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><strong>E-mail:</strong> Armazenado permanentemente para autenticação e comunicação</li>
                  <li><strong>Nome:</strong> Armazenado permanentemente para personalização da experiência</li>
                  <li><strong>Tipo de usuário:</strong> Identificação se é cliente ou terapeuta</li>
                  <li><strong>Token de acesso do Google Calendar (apenas terapeutas):</strong> Armazenado de forma criptografada para permitir sincronização contínua de eventos</li>
                </ul>
                <p className="text-[#496566] mb-4">
                  <strong>Medidas de segurança específicas:</strong>
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Tokens de acesso são criptografados usando algoritmos de nível industrial</li>
                  <li>Acesso aos tokens é restrito apenas a sistemas autorizados</li>
                  <li>Tokens são armazenados em servidores com certificação de segurança</li>
                  <li>Não armazenamos suas credenciais de login do Google (senha)</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">4.4 Compartilhamento de Dados do Google</h3>
                <p className="text-[#496566] mb-4">
                  <strong>Não compartilhamos, vendemos ou alugamos seus dados do Google com terceiros.</strong> Os dados obtidos 
                  através das APIs do Google são usados exclusivamente para as funcionalidades descritas acima.
                </p>
                <p className="text-[#496566] mb-4">
                  Compartilhamento limitado ocorre apenas nas seguintes situações:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><strong>Dentro da plataforma:</strong> Seu nome é visível para outros participantes durante as sessões terapêuticas</li>
                  <li><strong>Prestadores de serviço essenciais:</strong> Fornecedores de hospedagem em nuvem que armazenam dados de forma segura (com acordos de confidencialidade)</li>
                  <li><strong>Obrigações legais:</strong> Quando exigido por lei ou ordem judicial</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">4.5 Retenção e Exclusão de Dados do Google</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><strong>Retenção:</strong> Os dados do Google são mantidos enquanto sua conta estiver ativa</li>
                  <li><strong>Desconexão do Google Calendar:</strong> Terapeutas podem revogar o acesso ao calendário a qualquer momento através das configurações da conta. Isso remove o token de acesso dos nossos servidores</li>
                  <li><strong>Exclusão de conta:</strong> Ao excluir sua conta, todos os dados obtidos do Google são permanentemente removidos em até 30 dias</li>
                  <li><strong>Revogação de permissões:</strong> Você pode revogar o acesso da Constela Brasil à sua conta Google a qualquer momento através da página{' '}
                    <a href="https://myaccount.google.com/permissions" 
                       target="_blank" 
                       rel="noopener noreferrer" 
                       className="text-[#A56096] hover:underline">
                      Permissões da Conta Google
                    </a>
                  </li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">4.6 Limitações de Uso</h3>
                <p className="text-[#496566] mb-4">
                  Comprometemo-nos a usar os dados do Google apenas para os fins descritos acima. Especificamente:
                </p>
                <ul className="list-disc pl-6 text-[#496566]">
                  <li>Não usamos dados do Google para publicidade ou marketing direcionado</li>
                  <li>Não transferimos dados do Google para terceiros sem sua autorização expressa</li>
                  <li>Não usamos dados do Google para fins de treinamento de IA ou machine learning</li>
                  <li>Não acessamos mais informações do que o necessário para as funcionalidades oferecidas</li>
                  <li>Todo uso está em conformidade com a Política de Dados do Usuário dos Serviços da API do Google</li>
                </ul>
              </section>

              {/* Partilha de Dados */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">5. Compartilhamento de Dados com Outros Terceiros</h2>
                <p className="text-[#496566] mb-4">
                  Além dos serviços do Google descritos anteriormente, compartilhamos informações com outros prestadores 
                  de serviços essenciais apenas nas seguintes situações:
                </p>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">5.1 Prestadores de Serviços</h3>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><strong>Mercado Pago:</strong> Para processar pagamentos de forma segura (compartilhamos apenas dados necessários para transações)</li>
                  <li><strong>Hostinger:</strong> Para hospedagem e armazenamento seguro de dados na nuvem</li>
                  <li><strong>Jitsi Meet:</strong> Para videochamadas durante as sessões terapêuticas</li>
                  <li><strong>Serviços de e-mail:</strong> Para envio de notificações e comunicações autorizadas</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#3A5859] mb-4">5.2 Obrigações Legais</h3>
                <ul className="list-disc pl-6 text-[#496566]">
                  <li>Quando exigido por lei ou ordem judicial</li>
                  <li>Para proteger os nossos direitos, propriedade ou segurança</li>
                  <li>Para proteger a segurança de outros utilizadores</li>
                </ul>
              </section>

              {/* Segurança dos Dados */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">6. Segurança dos Dados</h2>
                <p className="text-[#496566] mb-4">
                  Implementamos medidas de segurança técnicas e organizacionais adequadas para proteger 
                  os seus dados pessoais contra acesso não autorizado, alteração, divulgação ou destruição:
                </p>
                <ul className="list-disc pl-6 text-[#496566]">
                  <li><strong>Encriptação:</strong> Todos os dados sensíveis são encriptados em trânsito e em repouso</li>
                  <li><strong>Controle de acesso:</strong> Acesso limitado aos dados apenas a funcionários autorizados</li>
                  <li><strong>Monitoramento:</strong> Sistemas de monitoramento contínuo para detectar atividades suspeitas</li>
                  <li><strong>Backups seguros:</strong> Cópias de segurança regulares e protegidas</li>
                  <li><strong>Atualizações de segurança:</strong> Manutenção regular dos sistemas de segurança</li>
                </ul>
              </section>

              {/* Os Seus Direitos */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">7. Os Seus Direitos (LGPD)</h2>
                <p className="text-[#496566] mb-4">
                  De acordo com a LGPD, você tem os seguintes direitos em relação aos seus dados pessoais:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li><strong>Direito de acesso:</strong> Solicitar informações sobre os dados que temos sobre você</li>
                  <li><strong>Direito de retificação:</strong> Corrigir dados incorretos ou desatualizados</li>
                  <li><strong>Direito de eliminação:</strong> Solicitar a exclusão dos seus dados (direito ao esquecimento)</li>
                  <li><strong>Direito à portabilidade:</strong> Receber os seus dados em formato estruturado</li>
                  <li><strong>Direito de oposição:</strong> Opor-se ao tratamento dos seus dados em certas situações</li>
                  <li><strong>Direito de limitação:</strong> Limitar o processamento dos seus dados</li>
                  <li><strong>Direito de revisão:</strong> Solicitar revisão de decisões automatizadas</li>
                </ul>
              </section>

              {/* Cookies */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">8. Cookies e Tecnologias de Rastreamento</h2>
                <p className="text-[#496566] mb-4">
                  Utilizamos cookies e tecnologias similares para:
                </p>
                <ul className="list-disc pl-6 text-[#496566] mb-6">
                  <li>Manter a sua sessão ativa durante a utilização da plataforma</li>
                  <li>Lembrar as suas preferências e configurações</li>
                  <li>Analisar o desempenho e utilização da plataforma</li>
                  <li>Melhorar a segurança dos nossos serviços</li>
                </ul>
                <p className="text-[#496566]">
                  Pode gerenciar as suas preferências de cookies através das configurações do seu navegador. 
                  Note que desativar certos cookies pode afetar a funcionalidade da plataforma.
                </p>
              </section>

              {/* Alterações à Política */}
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-[#3A5859] mb-6">9. Alterações à Política de Privacidade</h2>
                <p className="text-[#496566] mb-4">
                  Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças 
                  nos nossos serviços ou requisitos legais. Quando isso acontecer:
                </p>
                <ul className="list-disc pl-6 text-[#496566]">
                  <li>Notificaremos você por e-mail sobre alterações significativas</li>
                  <li>Atualizaremos a data de "última atualização" no topo desta página</li>
                  <li>As alterações entrarão em vigor 30 dias após a notificação</li>
                  <li>O seu uso contínuo dos serviços constituirá aceitação das alterações</li>
                </ul>
              </section>

              {/* Footer da página */}
              <div className="text-center pt-8 border-t border-[#E5E9E2]">
                <p className="text-sm text-[#496566]">
                  Esta Política de Privacidade é parte integrante dos nossos{' '}
                  <Link to="/termos-de-servico" className="text-[#A56096] hover:underline">
                    Termos de Serviço
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;